import { cva } from 'class-variance-authority';
import type { VariantProps } from 'class-variance-authority';
import { m } from 'motion/react';
import type { HTMLMotionProps } from 'motion/react';
import type { ReactNode } from 'react';

import { cn } from '../lib/cn';

/**
 * El botón del sistema.
 *
 * `busy` en vez de un spinner aparte: un botón que está trabajando se
 * deshabilita, lo dice y **mantiene su tamaño**, que es lo que impide que la
 * maqueta salte bajo el cursor justo en el momento en que se está pulsando. Por
 * eso el indicador se superpone en lugar de sustituir al contenido.
 *
 * El `whileTap` no es adorno: en una interfaz donde casi todo lo que se pulsa
 * dispara una petición, el hundimiento es la única confirmación inmediata que
 * no depende de la red.
 */
const button = cva(
  cn(
    'relative inline-flex select-none items-center justify-center gap-2 overflow-hidden',
    'font-semibold leading-none whitespace-nowrap',
    'transition-[color,background-color,border-color,box-shadow] duration-fast ease-out-soft',
    'disabled:pointer-events-none disabled:opacity-45',
  ),
  {
    variants: {
      variant: {
        primary: 'bg-primary text-white shadow-brand hover:bg-primary-dark',
        secondary: 'border border-line-strong/60 bg-raised text-ink hover:border-primary/50 hover:text-primary',
        quiet: 'bg-primary-pale text-primary hover:bg-primary/15',
        ghost: 'text-ink-soft hover:bg-muted hover:text-ink',
        danger: 'bg-danger text-white hover:brightness-110',
        'danger-ghost': 'border border-danger-line text-danger hover:bg-danger-pale',
        brand: 'brand-gradient text-white shadow-brand',
      },
      size: {
        sm: 'min-h-8 rounded-xs px-3 text-xs',
        md: 'min-h-10 rounded-sm px-4 text-sm',
        lg: 'min-h-12 rounded-md px-6 text-sm',
      },
      block: { true: 'w-full', false: '' },
    },
    defaultVariants: { variant: 'primary', size: 'md', block: false },
  },
);

/*
  Los atributos vienen de `HTMLMotionProps` y no de `ButtonHTMLAttributes`
  porque `motion` sustituye `style` por su propio `MotionStyle` —admite valores
  animables, que `CSSProperties` no tiene—, y con `exactOptionalPropertyTypes`
  los dos tipos no son compatibles. Tomarlos de la fuente evita la conversión.
*/
export type ButtonProps = { busy?: boolean | undefined; icon?: ReactNode } & VariantProps<typeof button> &
  HTMLMotionProps<'button'>;

export function Button({ busy = false, variant, size, block, icon, children, className, ...props }: ButtonProps) {
  // `children` llega como `ReactNode | MotionValue` porque `motion` permite
  // animar el contenido; un botón nunca lo hace, así que se estrecha aquí en
  // lugar de arrastrar el tipo ancho hasta el JSX.
  const content = children as ReactNode;

  return (
    <m.button
      type="button"
      whileTap={{ scale: 0.97 }}
      transition={{ type: 'spring', stiffness: 620, damping: 30 }}
      {...props}
      disabled={busy || props.disabled}
      aria-busy={busy || undefined}
      className={cn(button({ variant, size, block }), className)}
    >
      {/* La barra recorre el botón mientras hay una petición en vuelo. Dice
          «sigue pasando algo» sin mover ni un píxel del contenido. */}
      {busy ? (
        <span aria-hidden="true" className="absolute inset-x-0 bottom-0 h-0.5 overflow-hidden bg-white/25">
          <span className="animate-sweep block h-full w-1/3 bg-white/80" />
        </span>
      ) : null}

      {icon ? <span className="shrink-0 [&>svg]:size-4">{icon}</span> : null}
      {content}
    </m.button>
  );
}
